import { useCallback, useRef, useState } from 'react';

import LedgerAutocomplete from 'src/components/atoms/form/LedgerAutocomplete';
import { AgInput, AgSwitch } from 'src/components/ui';
import { LedgerAccountDetails, useLedgerAccounts } from 'src/modules/accounting/useLedgerAccounts';
import { useOrgSettings } from '../../orgSettings.store';
import SettingsSection from '../../SettingsSection';
import useUpdateFinanceConfiguration from './useUpdateFinanceConfiguration';

/** Optimistic wrapper: shows selected value immediately while mutation runs in background */
function useOptimisticAccount(serverValue: LedgerAccountDetails | undefined) {
  const [override, setOverride] = useState<LedgerAccountDetails | null | undefined>(undefined);
  const serverIdRef = useRef(serverValue?.id);

  // Clear override once server catches up
  if (serverValue?.id !== serverIdRef.current) {
    serverIdRef.current = serverValue?.id;
    if (override !== undefined && serverValue?.id === override?.id) {
      setOverride(undefined);
    }
  }

  const value = override !== undefined ? override : (serverValue ?? null);

  const setValue = useCallback((v: LedgerAccountDetails | null) => {
    setOverride(v);
  }, []);

  return [value, setValue] as const;
}

export default function PreferencesTab() {
  // Ensure the initial load of finance configuration has happened before rendering
  const loaded = useOrgSettings((state) => state.loaded);
  if (!loaded) return null;
  return <LoadedPreferencesTab />;
}

function LoadedPreferencesTab() {
  const financeConfiguration = useOrgSettings((state) => state.financeConfiguration);
  const updateFinanceConfiguration = useUpdateFinanceConfiguration();
  const ledgerAccounts = useLedgerAccounts();

  // Resolve default account objects from IDs
  const serverAssets = ledgerAccounts.find((a) => a.id === financeConfiguration?.defaultAssetsAccountId);
  const serverLiabilities = ledgerAccounts.find((a) => a.id === financeConfiguration?.defaultLiabilitiesAccountId);
  const serverExpenses = ledgerAccounts.find((a) => a.id === financeConfiguration?.defaultExpensesAccountId);
  const serverIncome = ledgerAccounts.find((a) => a.id === financeConfiguration?.defaultIncomeAccountId);
  const serverAdjustment = ledgerAccounts.find(
    (a) => a.id === financeConfiguration?.fractionalBalanceAdjustmentAccountId
  );

  // Optimistic state for instant UI feedback
  const [defaultAssetsAccount, setDefaultAssetsAccount] = useOptimisticAccount(serverAssets);
  const [defaultLiabilitiesAccount, setDefaultLiabilitiesAccount] = useOptimisticAccount(serverLiabilities);
  const [defaultExpensesAccount, setDefaultExpensesAccount] = useOptimisticAccount(serverExpenses);
  const [defaultIncomeAccount, setDefaultIncomeAccount] = useOptimisticAccount(serverIncome);
  const [fractionalBalanceAdjustmentAccount, setFractionalBalanceAdjustmentAccount] =
    useOptimisticAccount(serverAdjustment);

  const autoCloseEnabled = financeConfiguration?.autoCloseFractionalBalances ?? false;

  return (
    <div className="card col">
      <SettingsSection
        label="Default accounts"
        subtitle="These will be used to pre-fill relevant fields across payment documents (Invoices, Payables, etc.)"
        divider
      >
        <div className="flex flex-col gap-2 max-w-[560px]">
          <LedgerAutocomplete
            value={defaultAssetsAccount}
            onChange={(_, value) => {
              setDefaultAssetsAccount(value ?? null);
              updateFinanceConfiguration({ defaultAssetsAccountId: value?.id ?? null });
            }}
            options={ledgerAccounts}
            size="medium"
            label="Accounts Receivable (Invoices)"
            accountTypes={['CurrentAssets']}
          />
          <LedgerAutocomplete
            value={defaultLiabilitiesAccount}
            onChange={(_, value) => {
              setDefaultLiabilitiesAccount(value ?? null);
              updateFinanceConfiguration({ defaultLiabilitiesAccountId: value?.id ?? null });
            }}
            options={ledgerAccounts}
            size="medium"
            label="Accounts Payable (Payables)"
            accountTypes={['CurrentLiabilities']}
          />
          <LedgerAutocomplete
            value={defaultExpensesAccount}
            onChange={(_, value) => {
              setDefaultExpensesAccount(value ?? null);
              updateFinanceConfiguration({ defaultExpensesAccountId: value?.id ?? null });
            }}
            options={ledgerAccounts}
            size="medium"
            label="Default Expense Account"
            accountTypes={['DirectCosts', 'IndirectCosts', 'OtherCosts']}
          />
          <LedgerAutocomplete
            value={defaultIncomeAccount}
            onChange={(_, value) => {
              setDefaultIncomeAccount(value ?? null);
              updateFinanceConfiguration({ defaultIncomeAccountId: value?.id ?? null });
            }}
            options={ledgerAccounts}
            size="medium"
            label="Default Income Account"
            accountTypes={['Income', 'OtherIncome']}
          />
        </div>
      </SettingsSection>

      <SettingsSection
        label="Fractional balance auto-close"
        subtitle="Automatically close bills with small remaining balances caused by rounding differences between systems"
      >
        <div className="flex flex-col gap-4 max-w-[560px]">
          <AgSwitch
            isSelected={autoCloseEnabled}
            onChange={(isSelected) => updateFinanceConfiguration({ autoCloseFractionalBalances: isSelected })}
            label="Enable auto-close for fractional balances"
            size="md"
          />
          {autoCloseEnabled && (
            <>
              <AgInput
                label="Threshold (cents)"
                type="number"
                size="sm"
                className="w-[256px]"
                value={String(financeConfiguration?.fractionalBalanceThresholdCents ?? 5)}
                onChange={(value) => {
                  const parsed = parseInt(value, 10);
                  if (parsed > 0 && parsed < 100) {
                    updateFinanceConfiguration({ fractionalBalanceThresholdCents: parsed });
                  }
                }}
                helperText="Bills with remaining balances at or below this amount will be auto-closed"
              />
              <LedgerAutocomplete
                value={fractionalBalanceAdjustmentAccount}
                onChange={(_, value) => {
                  setFractionalBalanceAdjustmentAccount(value ?? null);
                  updateFinanceConfiguration({ fractionalBalanceAdjustmentAccountId: value?.id ?? null });
                }}
                options={ledgerAccounts}
                size="medium"
                label="Adjustment account"
              />
              {!fractionalBalanceAdjustmentAccount && (
                <span className="text-12 text-warning-10">
                  Select an adjustment account for auto-close to take effect.
                </span>
              )}
            </>
          )}
        </div>
      </SettingsSection>

      {/* TODO: Re-enable when precision level feature is implemented
      <SettingsSection
        label="Precision level"
        subtitle="This regulates number of decimal places for each numeric value across platform (ex. Pricing Sheets, Charges, etc.)"
        divider
      >
        <Hint title="Precision level coming soon!">
          <Select value={financeConfiguration?.precisionLevel} fullWidth disabled sx={{ maxWidth: '560px' }}>
            <MenuItem value={2}>2 decimal places ($0.00)</MenuItem>
          </Select>
        </Hint>
      </SettingsSection>

      <SettingsSection label="Currencies" subtitle="Select the default currency for transactions" divider>
        <Hint title="Default currency coming soon!">
          <Select value={financeConfiguration?.primaryCurrency} fullWidth disabled sx={{ maxWidth: '560px' }}>
            <MenuItem value="USD">$ USD</MenuItem>
            <MenuItem value="CAD">$ CAD</MenuItem>
            <MenuItem value="EUR">€ EUR</MenuItem>
          </Select>
        </Hint>
      </SettingsSection>
      */}
    </div>
  );
}
